import {useState, useEffect, SyntheticEvent} from 'react';
import './social-network-city-input.scss';
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  Box,
  InputAdornment,
  TextFieldPropsColorOverrides,
  Typography,
} from '@mui/material';
import {SocialNetworkCitiesRepository} from '../../repositories/social-network-cities';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {Search} from '@mui/icons-material';
import {OverridableStringUnion} from '@mui/types';
import FlatInput from '../flat-input/flat-input';

const UNKNOWN_LABEL = '(?)';

export interface SocialNetworkCityOption {
  cityId: string;
  cityName: string;
  countryName: string;
}

interface SocialNetworkCityInputProps {
  label: string;
  handleChange: (value?: SocialNetworkCityOption) => void;
  handleBlur?: () => void;
  error?: string;
  required?: boolean;
  value?: SocialNetworkCityOption;
  disabled?: boolean;
  disableClear?: boolean;
  disabledTooltip?: string;
  className?: string;
  id?: string;
  multiline?: boolean;
  color?: OverridableStringUnion<
    'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning',
    TextFieldPropsColorOverrides
  >;
  displayAdornment?: boolean;
}

export const SocialNetworkCityInput = ({
  id,
  className,
  label,
  value,
  multiline,
  color,
  displayAdornment = true,
  error,
  required,
  disabled,
  disableClear,
  disabledTooltip,
  handleChange,
  handleBlur,
}: SocialNetworkCityInputProps) => {
  // Hold state
  const [options, setOptions] = useState<Array<SocialNetworkCityOption>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState(value ? value.cityName : '');

  // Translations
  const {t} = useTranslation();

  // Fetch cities
  const handleFetchCities = async () => {
    try {
      // Start loading
      setLoading(true);

      // Get cities
      const response = await SocialNetworkCitiesRepository.instance.get(search, true);

      // Process response — group by country, sort alphabetically within each group
      const cityOptions: Array<SocialNetworkCityOption> = response
        .filter((option) => option.id)
        .map((option) => ({
          cityId: option.id!,
          cityName: option.name || UNKNOWN_LABEL,
          countryName: option.countryName || UNKNOWN_LABEL,
        }))
        .sort((a, b) => {
          const countryCompare = a.countryName.localeCompare(b.countryName);
          if (countryCompare !== 0) return countryCompare;
          return a.cityName.localeCompare(b.cityName);
        });

      // Set cities
      setOptions(cityOptions);
    } catch (error: any) {
      // Log
      console.debug('🔴 Error while fetching cities.', error);

      // Set empty values
      setOptions([]);
    } finally {
      // Stop loading
      setLoading(false);
    }
  };

  // Debounced get cities
  useEffect(() => {
    // Timeout
    let timeout: any;

    // Get cities after waiting 500ms
    timeout = setTimeout(() => handleFetchCities(), 500);

    // Clean up
    return () => {
      clearTimeout(timeout);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Handle search
  const handleSearch = (value: string, reason: AutocompleteInputChangeReason) => {
    // Only search on user input and not when the user selected an option or clear was pressed
    if (reason === 'input') {
      // Update search
      setSearch(value);
    } else if (reason === 'clear') {
      // Update search
      setSearch('');

      // Clear options
      setOptions([]);
    } else if (reason === 'reset' && value.trim().length === 0) {
      // Update search
      setSearch('');

      // Clear options
      setOptions([]);
    }
  };

  // Handle option
  const handleOption = (
    value: SocialNetworkCityOption | null | string,
    reason: AutocompleteChangeReason
  ) => {
    if (reason === 'clear') {
      // Clear value
      handleChange(undefined);

      // Clear options
      setSearch('');
      setOptions([]);
    } else {
      if (value && typeof value !== 'string') {
        // Update value
        handleChange(value);

        // Update search
        setSearch(value.cityName);
      }
    }
  };

  return (
    <Autocomplete
      id={id}
      className={className}
      options={options}
      // NOTE: Option label rendering as `undefined` on clicking `Enter` on an undefined option
      // https://github.com/mui/material-ui/issues/32042
      // @ts-ignore
      getOptionLabel={(option) => option.cityName ?? option}
      groupBy={(option) => option.countryName}
      value={value || null}
      isOptionEqualToValue={(option: SocialNetworkCityOption, value: SocialNetworkCityOption) =>
        option.cityId === value.cityId
      }
      onChange={(
        event: SyntheticEvent,
        value: SocialNetworkCityOption | null | string,
        reason: AutocompleteChangeReason
      ) => handleOption(value, reason)}
      onInputChange={(
        event: SyntheticEvent,
        value: string,
        reason: AutocompleteInputChangeReason
      ) => handleSearch(value, reason)}
      onBlur={handleBlur}
      loading={loading}
      disabled={disabled}
      disableClearable={disableClear}
      freeSolo
      renderInput={({InputProps, inputProps}) => (
        <FlatInput
          label={label}
          required={required}
          errors={error}
          multiline={multiline}
          color={color}
          fullWidth
          disabled={disabled}
          disabledTooltip={disabledTooltip}
          inputProps={inputProps}
          InputProps={{
            ...InputProps,
            startAdornment: displayAdornment ? (
              <>
                <InputAdornment
                  position="start"
                  sx={{
                    justifySelf: 'center',
                    height: 'auto',
                    marginTop: '0!important',
                  }}
                >
                  <Search sx={{color: 'var(--dcm-gray-general)'}} />
                </InputAdornment>
                {InputProps.startAdornment}
              </>
            ) : undefined,
          }}
        />
      )}
      renderGroup={(params) => (
        <li key={params.key}>
          <Box className="social-network-city-input-group-box">
            <Box className="social-network-city-input-group">
              <Typography variant="overline">{params.group}</Typography>
            </Box>
            <Box
              component="ul"
              className="social-network-city-input-options-box"
              {...params}
              key={undefined}
            >
              {params.children}
            </Box>
          </Box>
        </li>
      )}
      renderOption={(props, option) => {
        return (
          <li {...props}>
            <Box className="social-network-city-input-option-box">
              <Typography variant="body1">{option.cityName}</Typography>
            </Box>
          </li>
        );
      }}
      loadingText={t(translations.social_network_city_input.loading)}
      noOptionsText={t(translations.social_network_city_input.no_options)}
      sx={{
        width: '100%',
        '.MuiAutocomplete-inputRoot': {
          paddingTop: '8px',
        },
      }}
    />
  );
};
